import { type Input } from "antd";
import { type InjectedFormikProps } from "formik";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { displayWithFriendlyDefault } from "utilities/helpers";
import { isNullOrWhitespace } from "utilities/string/string";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTFormItemAutomatic, type ILabelSchema } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { InputEmail } from "commonComponents/entity/email/InputEmail/InputEmail";
import { type InputEmailFormValue } from "commonComponents/entity/email/InputEmail/InputEmail.types";
import { buildMailtoHref } from "commonComponents/entity/email/InputEmail/InputEmail.utils";
import { InputEmailList } from "commonComponents/entity/email/InputEmailList/InputEmailList";
import { type InputEmailListFormValue } from "commonComponents/entity/email/InputEmailList/InputEmailList.types";

interface IReadOnlyEmailRowProps {
    /**
     * Yup path to the email object — "primaryEmail" or "additionalEmails[N]".
     * The row builds nested ids "{rowId}.emailAddress" and "{rowId}.friendlyName"
     * so BTFormItemAutomatic resolves the schema and labels just like the
     * editable form does inside InputEmail.
     */
    rowId: string;
    emailAddress: string;
    friendlyName: string;
    /**
     * Optional explicit label for the email column. Used by the caller to
     * pass primaryEmailInputLabel for the primary row (carries the activation
     * popover). Omit to fall back to the schema-derived label, matching how
     * InputEmailList renders additional emails.
     */
    emailInputLabel?: string | JSX.Element | ((schema: ILabelSchema) => ReactNode) | null;
    emailTestId: string;
    useBds?: boolean;
}

/**
 * Two-column readonly view for an email entry: clickable mailto link on
 * the left, the optional friendly name (form "Label" value) on the right.
 * The "Label" column is omitted when the friendlyName is blank.
 *
 * Uses BTFormItemAutomatic with nested yup paths so the label visual
 * matches what InputEmail renders in the editable form.
 */
function ReadOnlyEmailRow({
    rowId,
    emailAddress,
    friendlyName,
    emailInputLabel,
    emailTestId,
    useBds,
}: IReadOnlyEmailRowProps) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-xs">
            <BTFormItemAutomatic
                id={`${rowId}.emailAddress`}
                label={emailInputLabel}
                useBds={useBds}
            >
                <BTExternalLink
                    href={buildMailtoHref(emailAddress, friendlyName)}
                    data-testid={emailTestId}
                >
                    {emailAddress}
                </BTExternalLink>
            </BTFormItemAutomatic>
            <BTFormItemAutomatic id={`${rowId}.friendlyName`} label="Label" useBds={useBds}>
                <span>{displayWithFriendlyDefault(friendlyName)}</span>
            </BTFormItemAutomatic>
        </div>
    );
}

export interface IEmailEntryFormValues {
    primaryEmail: InputEmailFormValue | null;
    additionalEmails: InputEmailListFormValue;
}
export interface IEmailEntryFormProps {
    readOnly?: boolean;
    inputFieldReadOnly?: boolean;
    showPrimaryEmailField: boolean;
    maxCount: number;
    innerGutterWidth?: number;
    primaryEmailInputLabel?: string | JSX.Element | ((schema: ILabelSchema) => ReactNode) | null;

    handleDeletePrimaryEmail?: () => void;
    useBds?: boolean;
}

export function EmailFormEntry(
    props: InjectedFormikProps<IEmailEntryFormProps, IEmailEntryFormValues>,
) {
    const {
        readOnly,
        values,
        setFieldValue,
        setFieldTouched,
        showPrimaryEmailField,
        maxCount,
        innerGutterWidth,
        primaryEmailInputLabel,
        inputFieldReadOnly,
        handleDeletePrimaryEmail,
        useBds,
    } = props;

    const [state, setState] = useState<{ showAdditionalEmails: boolean }>({
        showAdditionalEmails: !!values.primaryEmail?.emailAddress,
    });

    useEffect(() => {
        setState({
            showAdditionalEmails:
                values.additionalEmails.some((e) => !!e.emailAddress) ||
                !!values.primaryEmail?.emailAddress,
        });
    }, [values]);

    const emailFieldRef = useRef<Input>(null);
    const additionalEmailsId = "additionalEmails";
    const primaryEmailId = "primaryEmail";

    if (readOnly) {
        const primary =
            showPrimaryEmailField && !isNullOrWhitespace(values.primaryEmail?.emailAddress)
                ? values.primaryEmail!
                : undefined;
        // The form schema indexes additionalEmails by position, so preserve
        // the original index when filtering blanks — that index drives the
        // yup path BTFormItemAutomatic resolves against.
        const additionalIndexed = values.additionalEmails
            .map((email, index) => ({ email, index }))
            .filter(({ email }) => !isNullOrWhitespace(email.emailAddress));

        if (!primary && additionalIndexed.length === 0) {
            return null;
        }

        return (
            <div className="flex flex-col gap-sm">
                {primary && (
                    <ReadOnlyEmailRow
                        rowId={primaryEmailId}
                        emailAddress={primary.emailAddress}
                        friendlyName={primary.friendlyName}
                        emailInputLabel={primaryEmailInputLabel}
                        emailTestId="primaryEmailMailtoLink"
                        useBds={useBds}
                    />
                )}
                {additionalIndexed.map(({ email, index }) => (
                    <ReadOnlyEmailRow
                        key={`additional-email-${index}`}
                        rowId={`${additionalEmailsId}[${index}]`}
                        emailAddress={email.emailAddress}
                        friendlyName={email.friendlyName}
                        emailTestId={`additionalEmailMailtoLink-${index}`}
                        useBds={useBds}
                    />
                ))}
            </div>
        );
    }

    return (
        <BTRow>
            <BTCol span={24}>
                <>
                    {showPrimaryEmailField && values.primaryEmail && (
                        <InputEmail
                            id={primaryEmailId}
                            data-testid="primaryEmail"
                            isReadOnly={inputFieldReadOnly}
                            canDelete={false}
                            emailInputLabel={primaryEmailInputLabel}
                            labelInputLabel="Label"
                            gutterWidth={innerGutterWidth}
                            value={values.primaryEmail}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            onDelete={handleDeletePrimaryEmail}
                            emailFieldRef={emailFieldRef}
                        />
                    )}
                    {state.showAdditionalEmails && (
                        <InputEmailList
                            id={additionalEmailsId}
                            data-testid="additionalEmails"
                            isReadOnly={readOnly}
                            canDelete={!readOnly}
                            labelInputLabel="Label"
                            addEmailLabel="Add another email"
                            maxCount={maxCount}
                            value={values.additionalEmails}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                        />
                    )}
                </>
            </BTCol>
        </BTRow>
    );
}
